import React, { useMemo, useRef, useState } from 'react';
import {
    Box,
    ButtonBase,
    Collapse,
    IconButton,
    List,
    ListItemButton,
    Menu,
    MenuItem,
    Popover,
    Tooltip,
    Typography
} from "@mui/material";
import {
    ChevronLeft,
    ChevronRight,
    Close as CloseIcon,
    ExitToAppOutlined,
    ExpandMore,
    Logout as LogoutIcon,
    Settings as SettingsIcon,
    UnfoldMore
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { BetaPill } from "../BetaPill";
import { BrandMark } from "../BrandMark/BrandMark";
import { useBoundStore } from "../../store";
import { useLogoutPath } from "../../hooks/useLogoutPath";
import { usePortalPath } from "../../hooks/usePortalPath";
import {
    SideNavAccount,
    SideNavChild,
    SideNavItem,
    SideNavSection,
    SideNavWorkspace,
    bestActiveLink
} from "./sideNavModel";

// Rail geometry — matches the handoff (264 / 76). Kept here and re-exported so
// the AppShell reserves the same width for the permanent Drawer.
export const RAIL_WIDTH = 264;
export const RAIL_WIDTH_COLLAPSED = 76;

// Rail register — the spec's constant navy rail (PORTAL-THEME-PLAN.md §2.1a):
// identical in light and dark, so only the canvas flips. Values live in
// src/theme/tokens.ts; this map just names the roles the rail consumes.
const T = {
    rail: "var(--rail)",
    rule: "var(--rail-rule)",
    text: "var(--rail-text)",
    soft: "var(--rail-text-2)",
    mute: "var(--rail-muted)",
    activeBg: "var(--rail-active)",
    activeText: "var(--rail-accent)",
    hoverBg: "var(--rail-hover)",
    indicator: "var(--accent)",
    flyBg: "var(--rail-hover)",
    flyBorder: "var(--rail-rule)",
    flyShadow: "var(--sh-2)"
};

// Stroke-based nav SVGs inherit the row color when we retarget their stroke to
// currentColor (same trick the old mobile menu used).
const iconInherit = {
    "& svg [stroke]:not([stroke='none'])": { stroke: "currentColor" },
    "& svg": { display: "block" }
};

function ItemIcon({ node, size = 22 }: { node: React.ReactNode; size?: number }) {
    return (
        <Box
            sx={{
                width: size,
                height: size,
                flex: `0 0 ${size}px`,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                ...iconInherit
            }}
        >
            {node}
        </Box>
    );
}

// The 26px workspace mark — brand logo on a near-white chip so logo art
// stays legible on the navy rail (a plain chip when there is no logo).
function WorkspaceMark({ logo }: { logo?: string | null }) {
    return (
        <Box
            component={logo ? 'img' : 'div'}
            src={logo || undefined}
            alt=""
            sx={{
                width: 26,
                height: 26,
                flex: '0 0 26px',
                borderRadius: '7px',
                objectFit: 'contain',
                bgcolor: 'var(--rail-text)',
                p: logo ? '3px' : 0,
                display: 'block'
            }}
        />
    );
}

interface ChildRowProps {
    child: SideNavChild;
    active: boolean;
    variant: "inline" | "flyout";
    onNavigate: (link: string, external?: boolean) => void;
}

function ChildRow({ child, active, variant, onNavigate }: ChildRowProps) {
    const inline = variant === "inline";
    return (
        <ListItemButton
            disableGutters
            onClick={() => onNavigate(child.link, child.external)}
            sx={{
                minHeight: inline ? 34 : 36,
                px: 1.5,
                pl: inline ? "44px" : 1.5,
                gap: 1.25,
                borderRadius: "8px",
                color: active ? T.activeText : T.soft,
                bgcolor: active ? T.activeBg : "transparent",
                whiteSpace: "nowrap",
                "&:hover": { bgcolor: active ? T.activeBg : T.hoverBg },
                "&.Mui-focusVisible": {
                    outline: `2px solid ${T.indicator}`,
                    outlineOffset: "2px"
                }
            }}
        >
            {child.icon && <ItemIcon node={child.icon} size={18} />}
            <Typography
                sx={{
                    fontSize: 13.5,
                    fontWeight: active ? 600 : 500,
                    color: "inherit",
                    lineHeight: 1
                }}
            >
                {child.title}
            </Typography>
        </ListItemButton>
    );
}

interface NavRowProps {
    item: SideNavItem;
    collapsed: boolean;
    activeLink: string | null; // rail-wide best route match (longest link wins)
    open: boolean; // accordion open (expanded rail only)
    onToggleOpen: (key: string) => void;
    onOpen: (key: string) => void; // open without closing (row click)
    onNavigate: (link: string, external?: boolean) => void;
}

function NavRow({ item, collapsed, activeLink, open, onToggleOpen, onOpen, onNavigate }: NavRowProps) {
    const hasChildren = item.children.length > 0;
    const selfActive = !!item.link && item.link === activeLink;
    const childActiveKey = item.children.find((c) => c.link && c.link === activeLink)?.key;
    const isActive = selfActive || !!childActiveKey;

    // Collapsed rail: children live in a hover flyout (Popover). Hover-intent
    // timers keep it open while the pointer travels from the icon to the popover.
    const [flyEl, setFlyEl] = useState<HTMLElement | null>(null);
    const closeTimer = useRef<number | undefined>(undefined);
    const openFly = (el: HTMLElement) => {
        window.clearTimeout(closeTimer.current);
        setFlyEl(el);
    };
    const scheduleClose = () => {
        window.clearTimeout(closeTimer.current);
        closeTimer.current = window.setTimeout(() => setFlyEl(null), 120);
    };

    const handleClick = (e: React.MouseEvent<HTMLElement>) => {
        if (hasChildren && !collapsed) {
            if (item.link) {
                // Parents with a real link navigate (matches the old dropdown)
                // and reveal their children; the chevron handles closing.
                onOpen(item.key);
                onNavigate(item.link);
            } else {
                // Link-less parents (e.g. Tools): the row is just an expander.
                onToggleOpen(item.key);
            }
            return;
        }
        if (item.link) onNavigate(item.link);
        else if (hasChildren && collapsed) openFly(e.currentTarget);
    };

    const row = (
        <Box sx={{ position: "relative" }}>
            {isActive && (
                <Box
                    sx={{
                        position: "absolute",
                        left: collapsed ? 6 : -2,
                        top: "50%",
                        transform: "translateY(-50%)",
                        width: 3,
                        height: 20,
                        borderRadius: 999,
                        bgcolor: T.indicator
                    }}
                />
            )}
            <ListItemButton
                // Rendered as a div (ButtonBase still gives it role=button,
                // keyboard activation, focus ring and ripple) so the chevron
                // IconButton isn't a <button> nested in a <button> — nested
                // buttons double-fire activation, which defeats the chevron's
                // stopPropagation and navigates anyway.
                component="div"
                disableGutters
                selected={isActive}
                onClick={handleClick}
                onMouseEnter={collapsed && hasChildren ? (e) => openFly(e.currentTarget) : undefined}
                onMouseLeave={collapsed && hasChildren ? scheduleClose : undefined}
                sx={{
                    minHeight: 42,
                    px: collapsed ? 0 : 1.5,
                    justifyContent: collapsed ? "center" : "flex-start",
                    gap: 1.25,
                    borderRadius: "11px",
                    color: isActive ? T.activeText : T.soft,
                    bgcolor: isActive ? T.activeBg : "transparent",
                    transition: "background-color 120ms ease, color 120ms ease",
                    "&.Mui-selected": { bgcolor: T.activeBg, color: T.activeText },
                    "&.Mui-selected:hover": { bgcolor: T.activeBg },
                    "&:hover": { bgcolor: isActive ? T.activeBg : T.hoverBg, color: isActive ? T.activeText : T.text },
                    "&.Mui-focusVisible": {
                        outline: `2px solid ${T.indicator}`,
                        outlineOffset: "2px"
                    }
                }}
            >
                <ItemIcon node={item.icon} />
                {!collapsed && (
                    <>
                        <Typography
                            sx={{
                                flex: 1,
                                minWidth: 0,
                                fontSize: 14,
                                fontWeight: isActive ? 600 : 500,
                                color: "inherit",
                                lineHeight: 1,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis"
                            }}
                        >
                            {item.title}
                        </Typography>
                        {item.beta && <BetaPill />}
                        {hasChildren && (
                            <IconButton
                                size="small"
                                aria-label={open ? `Collapse ${item.title}` : `Expand ${item.title}`}
                                aria-expanded={open}
                                onClick={(e) => {
                                    // Chevron toggles the accordion WITHOUT navigating,
                                    // so users can open it and go straight to a child.
                                    e.stopPropagation();
                                    onToggleOpen(item.key);
                                }}
                                onMouseDown={(e) => e.stopPropagation()}
                                onTouchStart={(e) => e.stopPropagation()}
                                sx={{ p: 0.25, ml: item.beta ? 0 : "auto", flex: "none" }}
                            >
                                <ExpandMore
                                    sx={{
                                        fontSize: 16,
                                        color: T.mute,
                                        transition: "transform 160ms ease",
                                        transform: open ? "rotate(180deg)" : "none"
                                    }}
                                />
                            </IconButton>
                        )}
                    </>
                )}
            </ListItemButton>
        </Box>
    );

    return (
        <Box>
            {collapsed ? (
                <Tooltip title={hasChildren ? "" : item.title} placement="right" arrow>
                    {row}
                </Tooltip>
            ) : (
                row
            )}

            {/* Expanded rail: inline accordion */}
            {!collapsed && hasChildren && (
                <Collapse in={open} timeout={180} unmountOnExit>
                    <List
                        disablePadding
                        sx={{ display: "flex", flexDirection: "column", gap: "2px", pt: "3px", pb: "6px" }}
                    >
                        {item.children.map((c) => (
                            <ChildRow
                                key={c.key}
                                child={c}
                                active={c.key === childActiveKey}
                                variant="inline"
                                onNavigate={onNavigate}
                            />
                        ))}
                    </List>
                </Collapse>
            )}

            {/* Collapsed rail: hover flyout */}
            {collapsed && hasChildren && (
                <Popover
                    open={Boolean(flyEl)}
                    anchorEl={flyEl}
                    onClose={() => setFlyEl(null)}
                    anchorOrigin={{ vertical: "top", horizontal: "right" }}
                    transformOrigin={{ vertical: "top", horizontal: "left" }}
                    disableRestoreFocus
                    disableScrollLock
                    slotProps={{
                        paper: {
                            // Portaled to <body>, so it sits outside .MuiDrawer-paper —
                            // this class re-applies the rail's theme-independent tokens.
                            className: "rail-flyout",
                            onMouseEnter: () => window.clearTimeout(closeTimer.current),
                            onMouseLeave: scheduleClose,
                            sx: {
                                pointerEvents: "auto",
                                ml: "10px",
                                minWidth: 212,
                                p: 1,
                                borderRadius: "12px",
                                bgcolor: T.flyBg,
                                backgroundImage: "none",
                                border: `1px solid ${T.flyBorder}`,
                                boxShadow: T.flyShadow,
                                overflow: "visible"
                            }
                        }
                    }}
                    sx={{ pointerEvents: "none" }}
                >
                    <Box sx={{ pointerEvents: "auto" }}>
                        <Typography
                            sx={{
                                font: "700 10.5px/1 CircularStd, sans-serif",
                                letterSpacing: ".07em",
                                textTransform: "uppercase",
                                color: T.mute,
                                px: 1.5,
                                pt: 0.75,
                                pb: 1
                            }}
                        >
                            {item.title}
                        </Typography>
                        <List disablePadding sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                            {item.children.map((c) => (
                                <ChildRow
                                    key={c.key}
                                    child={c}
                                    active={c.key === childActiveKey}
                                    variant="flyout"
                                    onNavigate={(link, external) => {
                                        setFlyEl(null);
                                        onNavigate(link, external);
                                    }}
                                />
                            ))}
                        </List>
                    </Box>
                </Popover>
            )}
        </Box>
    );
}

export interface SideNavContentProps {
    sections: SideNavSection[];
    activePath: string;
    workspace: SideNavWorkspace | null;
    account: SideNavAccount | null;
    collapsed: boolean;
    mobile?: boolean;
    onToggleCollapse?: () => void;
    onClose?: () => void;
}

export function SideNavContent({
    sections,
    activePath,
    workspace,
    account,
    collapsed,
    mobile = false,
    onToggleCollapse,
    onClose
}: SideNavContentProps) {
    const navigate = useNavigate();
    const logout = useBoundStore((s) => s.logout);
    const logoutPath = useLogoutPath();
    const { p } = usePortalPath();
    const [acctEl, setAcctEl] = useState<null | HTMLElement>(null);

    // One rail-wide active row: the longest link that matches the route, so
    // flat siblings sharing a prefix (/experts vs /experts/featured) don't
    // both light up.
    const activeLink = useMemo(() => {
        const links = sections.flatMap((section) =>
            section.items.flatMap((it) => [
                ...(it.link ? [it.link] : []),
                ...it.children.filter((c) => c.link && !c.external).map((c) => c.link)
            ])
        );
        return bestActiveLink(links, activePath);
    }, [sections, activePath]);

    // Which parent's accordion is expanded (expanded rail only). Default to the
    // parent that owns the active route.
    const initialOpen = useMemo(() => {
        const owner = sections
            .flatMap((section) => section.items)
            .find(
                (it) => it.children.length > 0 && it.children.some((c) => c.link && c.link === activeLink)
            );
        return owner ? owner.key : null;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sections]);
    const [openKey, setOpenKey] = useState<string | null>(initialOpen);

    const showLabels = !collapsed; // mobile is always expanded
    // Internal staff inside a brand-portal workspace get a way back out.
    const workspaceExit = workspace && workspace.exit ? workspace.exit : null;

    const onNavigate = (link: string, external?: boolean) => {
        if (!link) return;
        if (external) {
            window.open(link, "_blank", "noopener,noreferrer");
        } else {
            navigate(link);
        }
        if (mobile && onClose) onClose();
    };

    const toggleOpen = (key: string) => setOpenKey((cur) => (cur === key ? null : key));

    const handleLogout = async () => {
        setAcctEl(null);
        await logout();
        // A brand client returns to their own branded login, not the TDC one.
        navigate(logoutPath);
    };

    return (
        <Box
            component="nav"
            sx={{
                height: "100%",
                width: "100%",
                bgcolor: T.rail,
                display: "flex",
                flexDirection: "column",
                fontFamily: "CircularStd, sans-serif"
            }}
        >
            {/* Header — mark + wordmark + collapse / close toggle */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: collapsed ? "center" : "space-between",
                    gap: 1,
                    height: 61,
                    px: collapsed ? 0 : "16px",
                    borderBottom: `1px solid ${T.rule}`,
                    flex: "none"
                }}
            >
                {showLabels && (
                    <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0, cursor: "pointer" }}
                        onClick={() => onNavigate(p("/dashboard"))}
                    >
                        {/* The rail is navy in BOTH themes, so the mark is gold
                            in both — the raw asset is black and disappeared here
                            even in light mode. */}
                        <BrandMark height={26} color="var(--rail-accent)" />
                        <Typography
                            sx={{
                                fontSize: 15,
                                fontWeight: 700,
                                color: T.text,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis"
                            }}
                        >
                            The Desire Company
                        </Typography>
                    </Box>
                )}
                {mobile ? (
                    <IconButton
                        onClick={onClose}
                        aria-label="Close navigation"
                        sx={{ color: T.text, bgcolor: T.hoverBg }}
                    >
                        <CloseIcon />
                    </IconButton>
                ) : collapsed ? (
                    // Collapsed: the header slot holds only the expand affordance (1b).
                    <IconButton
                        onClick={onToggleCollapse}
                        aria-label="Expand navigation"
                        sx={{ color: T.mute }}
                        size="small"
                    >
                        <ChevronRight />
                    </IconButton>
                ) : (
                    <IconButton
                        onClick={onToggleCollapse}
                        aria-label="Collapse navigation"
                        sx={{ color: T.mute }}
                        size="small"
                    >
                        <ChevronLeft />
                    </IconButton>
                )}
            </Box>

            {/* Collapsed rail keeps the workspace mark (icon only); with an
                exit it doubles as the "Exit workspace" button */}
            {workspace && collapsed && !mobile && (
                <Box sx={{ display: 'flex', justifyContent: 'center', pt: '10px', pb: '4px', flex: 'none' }}>
                    <Tooltip
                        title={workspaceExit ? `${workspace.name} · ${workspaceExit.label}` : workspace.name}
                        placement="right"
                        arrow
                    >
                        {workspaceExit ? (
                            <ButtonBase
                                aria-label={workspaceExit.label}
                                onClick={() => onNavigate(workspaceExit.link)}
                                sx={{ borderRadius: '7px' }}
                            >
                                <WorkspaceMark logo={workspace.logo} />
                            </ButtonBase>
                        ) : (
                            <Box>
                                <WorkspaceMark logo={workspace.logo} />
                            </Box>
                        )}
                    </Tooltip>
                </Box>
            )}

            {/* Workspace card (brand roles: their own brand; internal staff:
                the brand-portal workspace they opened, with an exit) */}
            {workspace && showLabels && (
                <Box sx={{ px: '12px', pt: '12px', pb: '4px', flex: 'none' }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            background: T.hoverBg,
                            gap: 1.25,
                            height: 50,
                            px: 1,
                            borderRadius: '11px'
                        }}
                    >
                        <WorkspaceMark logo={workspace.logo} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                                sx={{
                                    font: '700 9px/1 CircularStd, sans-serif',
                                    letterSpacing: '.09em',
                                    textTransform: 'uppercase',
                                    color: T.mute
                                }}
                            >
                                Workspace
                            </Typography>
                            <Typography
                                sx={{
                                    mt: "4px",
                                    fontSize: 14,
                                    fontWeight: 700,
                                    color: T.text,
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis"
                                }}
                            >
                                {workspace.name}
                            </Typography>
                        </Box>
                        {workspaceExit && (
                            <Tooltip title={workspaceExit.label} placement="right" arrow>
                                <IconButton
                                    size="small"
                                    aria-label={workspaceExit.label}
                                    onClick={() => onNavigate(workspaceExit.link)}
                                    sx={{
                                        color: T.mute,
                                        flex: 'none',
                                        '&:hover': { color: T.text, bgcolor: T.activeBg }
                                    }}
                                >
                                    <ExitToAppOutlined sx={{ fontSize: 18 }} />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>
                </Box>
            )}

            {/* Nav list — labeled sections (uppercase headers when expanded,
                thin dividers when collapsed); unlabeled sections render flush */}
            <List
                sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: "auto",
                    overflowX: "visible",
                    display: "flex",
                    flexDirection: "column",
                    gap: "2px",
                    px: collapsed ? "12px" : "10px",
                    py: "10px"
                }}
            >
                {sections.map((section) => (
                    <React.Fragment key={section.key}>
                        {section.label &&
                            (showLabels ? (
                                <Typography
                                    sx={{
                                        font: "700 10.5px/1 CircularStd, sans-serif",
                                        letterSpacing: ".07em",
                                        textTransform: "uppercase",
                                        color: T.mute,
                                        px: 1.5,
                                        pt: "16px",
                                        pb: "6px"
                                    }}
                                >
                                    {section.label}
                                </Typography>
                            ) : (
                                <Box sx={{ height: "1px", bgcolor: T.rule, mx: "10px", my: "8px", flex: "none" }} />
                            ))}
                        {section.items.map((item) => (
                            <NavRow
                                key={item.key}
                                item={item}
                                collapsed={collapsed}
                                activeLink={activeLink}
                                open={openKey === item.key}
                                onToggleOpen={toggleOpen}
                                onOpen={setOpenKey}
                                onNavigate={onNavigate}
                            />
                        ))}
                    </React.Fragment>
                ))}
            </List>

            {/* Account block — pinned bottom */}
            {account && (
                <>
                    <Box
                        role="button"
                        tabIndex={0}
                        onClick={(e) => setAcctEl(e.currentTarget)}
                        sx={{
                            mt: "auto",
                            display: "flex",
                            alignItems: "center",
                            gap: 1.25,
                            borderTop: `1px solid ${T.rule}`,
                            px: collapsed ? 0 : "14px",
                            py: "12px",
                            justifyContent: collapsed ? "center" : "flex-start",
                            cursor: "pointer",
                            "&:hover": { bgcolor: T.hoverBg }
                        }}
                    >
                        {account.avatar ? (
                            <Box
                                component="img"
                                src={account.avatar}
                                alt=""
                                sx={{
                                    width: 36,
                                    height: 36,
                                    flex: "0 0 36px",
                                    borderRadius: 999,
                                    objectFit: "cover"
                                }}
                            />
                        ) : (
                            <Box
                                sx={{
                                    width: 36,
                                    height: 36,
                                    flex: "0 0 36px",
                                    borderRadius: 999,
                                    bgcolor: "var(--tint)",
                                    color: "var(--accent-text)",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    font: "700 13px/1 CircularStd, sans-serif"
                                }}
                            >
                                {account.initials}
                            </Box>
                        )}
                        {showLabels && (
                            <>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography
                                        sx={{
                                            fontSize: 13.5,
                                            fontWeight: 600,
                                            color: T.text,
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis"
                                        }}
                                    >
                                        {account.name}
                                    </Typography>
                                    {account.role && (
                                        <Typography
                                            sx={{
                                                fontSize: 12,
                                                color: T.soft,
                                                whiteSpace: "nowrap",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis"
                                            }}
                                        >
                                            {account.role}
                                        </Typography>
                                    )}
                                </Box>
                                <UnfoldMore sx={{ fontSize: 16, color: T.mute }} />
                            </>
                        )}
                    </Box>
                    <Menu
                        anchorEl={acctEl}
                        open={Boolean(acctEl)}
                        onClose={() => setAcctEl(null)}
                        anchorOrigin={{ vertical: "top", horizontal: "right" }}
                        transformOrigin={{ vertical: "bottom", horizontal: "left" }}
                    >
                        <MenuItem
                            onClick={() => {
                                setAcctEl(null);
                                navigate("/settings");
                            }}
                        >
                            <SettingsIcon fontSize="small" />
                            <Typography sx={{ ml: 1 }}>Settings</Typography>
                        </MenuItem>
                        <MenuItem onClick={handleLogout}>
                            <LogoutIcon fontSize="small" color="error" />
                            <Typography sx={{ ml: 1 }} color="error">
                                Log out
                            </Typography>
                        </MenuItem>
                    </Menu>
                </>
            )}
        </Box>
    );
}
